import { translations, Language } from '../config/translations';
import { buildApiUrl } from '../config/environment';

// Translation helper function
export const createTranslationFunction = (language: Language) => {
  return (key: keyof typeof translations.en) => translations[language][key] || translations.en[key];
};

// Tesla name cleaning utility
export const cleanTeslaName = (originalName: string, operatorId?: number): string => {
  // Check if this is a Tesla Supercharger by operator ID or name
  const isTesla = operatorId === 23 || operatorId === 3534 || operatorId === 27 ||
                 originalName.toLowerCase().includes('tesla') || 
                 originalName.toLowerCase().includes('supercharger');
  
  if (!isTesla) {
    return originalName;
  }
  
  // Remove "Tesla", "Supercharger", dashes, and extra spaces
  let cleanedName = originalName
    .replace(/tesla/gi, '')
    .replace(/supercharger/gi, '')
    .replace(/[-–—]/g, '') // Remove various types of dashes
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
  
  // If we have a cleaned name, format it as "Tesla Supercharger - [Location]"
  if (cleanedName && cleanedName !== '') {
    return `Tesla Supercharger - ${cleanedName}`;
  }
  
  // Fallback to original name if cleaning resulted in empty string
  return originalName;
};

// Network fetching utility
export const getNetworks = async (setSelectedNetworks: (networks: number[]) => void): Promise<number[]> => {
  try {
    const response = await fetch(buildApiUrl('/api/chargers/networks'), {
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      setSelectedNetworks(data.operatorIds);
      return data.operatorIds;
    }
  } catch (error) {
    console.error('Failed to get networks:', error);
  }
  return [23, 3534]; // Default to both Tesla operator IDs
};

// Navigation utility functions
export const openTeslaNavigation = (latitude: number, longitude: number, name: string): void => {
  const teslaUrl = `https://tesla.com/teslaapp?share=destination&lat=${latitude}&lng=${longitude}&name=${encodeURIComponent(name)}`;
  window.open(teslaUrl, '_blank');
};

export const openTeslaNavigationDirect = (latitude: number, longitude: number, name: string): void => {
  const teslaUrl = `https://www.tesla.com/navigation?lat=${latitude}&lng=${longitude}&name=${encodeURIComponent(name)}`;
  window.open(teslaUrl, '_blank');
};

export const openGoogleMapsNavigation = (latitude: number, longitude: number, name: string): void => {
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving&destination_place_id=${encodeURIComponent(name)}`;
  window.open(googleUrl, '_blank');
};

// Availability status formatting utility
export const formatAvailabilityStatus = (availability: any): { text: string; color: string } | null => {
  if (!availability) return null;
  
  let statusText = '';
  let statusColor = 'text-gray-500';
  
  if (availability.available !== null) {
    if (availability.available) {
      statusText = 'Available';
      statusColor = 'text-green-600';
    } else {
      statusText = 'Limited';
      statusColor = 'text-yellow-600';
    }
  } else if (availability.total) {
    statusText = `${availability.total} stalls`;
    statusColor = 'text-blue-600';
  } else {
    statusText = 'Status unknown';
    statusColor = 'text-gray-500';
  }
  
  return { text: statusText, color: statusColor };
};